import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Table,
  Card,
  Button,
  Modal,
  InputNumber,
  Space,
  Input,
  message,
  Form,
  Tooltip,
  Typography,
  Row,
  Col,
  Descriptions,
  Spin,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CalculatorOutlined,
  LockOutlined,
  SyncOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useRootStore } from "../store/root-store";
import { useAuth } from '../context/AuthContext';
import { api } from '../services/authService';
import { formatDate } from '../utils/format';
import useGetExchangeRates from '../hooks/useGetExchangeRates';
import useGetExchangeRate from '../hooks/useGetExchangeRate';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { isSuperAdmin, isAdmin } from '../utils/permissions';

const { Title, Text } = Typography;

const ScrollContainer = styled.div`
  scroll-behavior: smooth;

  @media (max-width: 768px) {
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }
  }
`;

const StyledCard = styled(Card)`
  border-radius: 8px;
  background: #fff;

  .ant-card-head {
    min-height: 48px;
    padding: 0 24px;
    border-bottom: 0;
  }

  .ant-card-body {
    padding: 24px;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 16px;
    }
  }
`;

function ExchangeRate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user profile to get user ID
  const { data: userProfile } = useQuery({
    queryKey: ['admin-profile'],
    queryFn: async () => {
      const response = await api.get('/admin/user-profile');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user roles
  const { data: userRoles = [] } = useQuery({
    queryKey: ['user-roles', userProfile?.id],
    queryFn: async () => {
      if (!userProfile?.id) return [];
      const response = await api.get(`/admin/user-roles?user_id=${userProfile.id}`);
      return response.data.user_roles || [];
    },
    enabled: !!userProfile?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Check if user has admin or super admin privileges
  console.log("=== User Roles Debug ===");
  console.log("User roles:", userRoles);
  console.log("User profile:", userProfile);
  console.log("User profile role:", userProfile?.role);
  console.log("User profile is_super:", userProfile?.is_super);
  console.log("User profile status:", userProfile?.status);
  
  // Check if user has super admin privileges using centralized functions
  const hasSuperAdminAccess = isSuperAdmin(userProfile, userRoles);
  const hasAdminAccess = isAdmin(userProfile, userRoles);
  
  console.log("Has super admin access:", hasSuperAdminAccess);
  console.log("Has admin access:", hasAdminAccess);
  console.log("=== End User Roles Debug ===");

  const [detailsModal, setDetailsModal] = useState({ visible: false, rateId: null });
  const [editState, setEditState] = useState({ editing: false, rate: null, spread: null, spread_type: null, is_reversed: false, saving: false });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [isReversed, setIsReversed] = useState(false);
  
  // Filter modal state
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  
  // Currency filtering state
  const [filters, setFilters] = useState({
    source_currency_code: '',
    dest_currency_code: ''
  });
  
  // Local input state for better UX
  const [filterInputs, setFilterInputs] = useState({
    source_currency_code: '',
    dest_currency_code: ''
  });

  // Use React Query hooks with filters
  const {
    data: rates,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError: isListError,
    error: listError,
    refetch: refetchRates,
  } = useGetExchangeRates(filters);

  const {
    data: rateDetails,
    isLoading: detailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useGetExchangeRate(detailsModal.rateId);

  // Mutation for updating exchange rate
  const updateRateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(`/admin/rates/${id}`, data);
      return response.data;
    },
    onSuccess: (updatedRate) => {
      // Update the query cache immediately for real-time updates
      queryClient.setQueryData(['exchange-rate', detailsModal.rateId], (oldData) => ({
        ...oldData,
        ...updatedRate
      }));
      
      // Also invalidate to ensure fresh data from server
      queryClient.invalidateQueries({
        queryKey: ['exchange-rate', detailsModal.rateId],
        exact: true
      });
      queryClient.invalidateQueries({
        queryKey: ['exchange-rates'],
        exact: true
      });
      
      // Refresh the rates list after successful edit
      refetchRates();
      
      setEditState({ editing: false, rate: updatedRate.rate, spread: updatedRate.spread, spread_type: updatedRate.spread_type, is_reversed: updatedRate.is_reversed, saving: false });
      message.success('Exchange rate updated');
    },
    onError: (error) => {
      console.error('Failed to update exchange rate:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update exchange rate';
      message.error(errorMessage);
      setEditState(prev => ({ ...prev, saving: false }));
    }
  });

  const createRateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post("/admin/rates", data);
      return response.data;
    },
    onSuccess: () => {
      setCreateModalVisible(false);
      createForm.resetFields();
      refetchRates();
      message.success("Exchange rate created");
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || "Failed to create exchange rate";
      message.error(errorMessage);
    },
    onSettled: () => setCreating(false),
  });

  const handleCreate = () => {
    createForm.validateFields().then((values) => {
      setCreating(true);
      // Ensure currency codes are uppercase
      const payload = {
        ...values,
        source_currency_code: values.source_currency_code.toUpperCase(),
        dest_currency_code: values.dest_currency_code.toUpperCase(),
        is_reversed: isReversed,
      };
      createRateMutation.mutate(payload);
    });
  };

  // Infinite scroll handler
  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Open details modal
  const openDetails = (rateId) => {
    setDetailsModal({ visible: true, rateId });
  };

  // Close details modal
  const closeDetails = () => {
    setDetailsModal({ visible: false, rateId: null });
    setEditState({ editing: false, rate: null, spread: null, spread_type: null, is_reversed: false, saving: false });
  };

  // Initialize edit state when rate details load
  useEffect(() => {
    if (rateDetails && detailsModal.visible) {
      setEditState({ 
        editing: false, 
        rate: rateDetails.rate, 
        spread: rateDetails.spread, 
        spread_type: rateDetails.spread_type || 'flat', 
        is_reversed: rateDetails.is_reversed || false,
        saving: false 
      });
    }
  }, [rateDetails, detailsModal.visible]);

  // Save edits - only send changed fields
  const saveEdit = async () => {
    setEditState(prev => ({ ...prev, saving: true }));
    
    // Build payload with only changed fields
    const payload = {};
    
    if (editState.rate !== rateDetails.rate) {
      payload.rate = editState.rate;
    }
    
    if (editState.spread !== rateDetails.spread) {
      payload.spread = editState.spread;
    }
    
    if (editState.spread_type !== rateDetails.spread_type) {
      payload.spread_type = editState.spread_type;
    }
    
    if (editState.is_reversed !== rateDetails.is_reversed) {
      payload.is_reversed = editState.is_reversed;
    }
    
    // If spread is in payload, ensure spread_type is also included (API requirement)
    if (payload.spread !== undefined && payload.spread_type === undefined) {
      payload.spread_type = editState.spread_type || rateDetails.spread_type || 'flat';
    }
    
    // Only make API call if there are changes
    if (Object.keys(payload).length > 0) {
      updateRateMutation.mutate({
        id: detailsModal.rateId,
        data: payload
      });
    } else {
      // No changes made
      setEditState(prev => ({ ...prev, editing: false, saving: false }));
      message.info('No changes to save');
    }
  };

  // Handle filter input changes (local state only)
  const handleFilterInputChange = (key, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply filters (triggers API call)
  const applyFilters = () => {
    setFilters({
      source_currency_code: filterInputs.source_currency_code.trim().toUpperCase(),
      dest_currency_code: filterInputs.dest_currency_code.trim().toUpperCase()
    });
    setFilterModalVisible(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterInputs({
      source_currency_code: '',
      dest_currency_code: ''
    });
    setFilters({
      source_currency_code: '',
      dest_currency_code: ''
    });
  };

  // Handle refresh - clear filters and reset listing
  const handleRefresh = () => {
    clearFilters();
    refetchRates();
  };

  // Handle Enter key in filter inputs
  const handleFilterKeyPress = (e, key) => {
    if (e.key === 'Enter') {
      applyFilters();
    }
  };

  // Check if filters are active
  const hasActiveFilters = filters.source_currency_code || filters.dest_currency_code;

  // Show error message if list fails to load
  if (isListError) {
    message.error(listError?.message || 'Failed to fetch exchange rates');
  }

  // Table columns
  const columns = [
    {
      title: 'Source Currency',
      dataIndex: 'source_currency_code',
      key: 'source_currency_code',
      render: (code, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={`https://wise.com/public-resources/assets/flags/rectangle/${code.toLowerCase()}.png`}
            alt={code}
            style={{ width: 24, height: 16, borderRadius: 2, flexShrink: 0 }}
          />
          <Typography.Text strong>{code}</Typography.Text>
        </div>
      ),
    },
    {
      title: 'Destination Currency',
      dataIndex: 'dest_currency_code',
      key: 'dest_currency_code',
      render: (code, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={`https://wise.com/public-resources/assets/flags/rectangle/${code.toLowerCase()}.png`}
            alt={code}
            style={{ width: 24, height: 16, borderRadius: 2, flexShrink: 0 }}
          />
          <Typography.Text strong>{code}</Typography.Text>
        </div>
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate) => rate,
    },
    {
      title: 'Spread',
      dataIndex: 'spread',
      key: 'spread',
      render: (spread, record) => (
        record.spread_type === 'percentage' ? `${spread}%` : spread
      ),
    },
    {
      title: 'Total Rate',
      key: 'total_rate',
      render: (_, record) => record.total_rate,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated',
      key: 'updated',
      render: (updated) => formatDate(updated),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => openDetails(record.id)}>
          View Details
        </Button>
      ),
    },
  ];

  // Add a new component for the access denied overlay
  const AccessDeniedOverlay = () => (
    <div
      style={{
        position: "absolute",
        top: "64px", // Start below the top navigation bar
        left: "80px", // Start after sidebar (assuming collapsed sidebar width)
        right: 0,
        bottom: 0,
        backdropFilter: "blur(6px)",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 90, // Lower z-index to avoid hiding navigation elements
        pointerEvents: "all", // Allow clicking on the overlay but not through it
      }}
    >
      <div
        style={{
          background: "white",
          padding: "30px",
          borderRadius: "8px",
          textAlign: "center",
          maxWidth: "400px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <LockOutlined
          style={{ fontSize: "48px", color: "#ff4d4f", marginBottom: "20px" }}
        />
        <h2>Access Denied</h2>
        <p style={{ fontSize: "16px", margin: "10px 0 20px" }}>
          Admin privileges required to access this page.
        </p>
        <Button type="primary" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    </div>
  );

  return (
    <ScrollContainer>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <StyledCard bordered={false}>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} lg={12}>
                <Title level={4} style={{ margin: 0 }}>
                  Exchange Rate Management
                </Title>
                <Text type="secondary">
                  Manage exchange rates and spreads
                </Text>
              </Col>
              <Col xs={24} lg={12}>
                <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                  <Button
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={() => setCreateModalVisible(true)}
                    disabled={!hasSuperAdminAccess}
                  >
                    Create Exchange Rate
                  </Button>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    Filter
                    {hasActiveFilters && (
                      <span style={{ marginLeft: 4, background: '#1890ff', color: 'white', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
                        {[filters.source_currency_code, filters.dest_currency_code].filter(Boolean).length}
                      </span>
                    )}
                  </Button>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={handleRefresh}
                    loading={isFetching && !isFetchingNextPage}
                  >
                    Refresh
                  </Button>
                </Space>
              </Col>
            </Row>

            <div style={{ marginTop: 24, maxHeight: 600, overflow: 'auto', position: 'relative' }} onScroll={handleTableScroll}>
              <Table
                dataSource={rates}
                columns={columns}
                loading={isFetching && !isFetchingNextPage}
                rowKey="id"
                pagination={false}
              />
              {isFetchingNextPage && (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin />
                </div>
              )}
            </div>
            <Modal
              title={rateDetails ? `${rateDetails.source_currency_name} to ${rateDetails.dest_currency_name}` : 'Exchange Rate Details'}
              open={detailsModal.visible}
              onCancel={closeDetails}
              footer={null}
            >
              {detailsLoading ? (
                <Spin />
              ) : isDetailsError ? (
                <Typography.Text type="danger">{detailsError?.message || 'Failed to fetch exchange rate details'}</Typography.Text>
              ) : rateDetails ? (
                <div>
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Source Currency">
                      {rateDetails.source_currency_name} ({rateDetails.source_currency_code})
                    </Descriptions.Item>
                    <Descriptions.Item label="Destination Currency">
                      {rateDetails.dest_currency_name} ({rateDetails.dest_currency_code})
                    </Descriptions.Item>
                    <Descriptions.Item label="Rate">
                      {editState.editing ? (
                        <div>
                          <InputNumber
                            value={editState.rate}
                            onChange={val => setEditState(prev => ({ ...prev, rate: val }))}
                            min={0}
                            style={{ width: 120, marginBottom: 8 }}
                            placeholder={editState.is_reversed ? "e.g. 1500" : "e.g. 0.0006"}
                          />
                          <div style={{ fontSize: '12px', color: '#586069', marginTop: 4 }}>
                            {editState.is_reversed ? (
                              `Reverse: ${rateDetails.dest_currency_code} per 1 ${rateDetails.source_currency_code}`
                            ) : (
                              `Standard: 1 ${rateDetails.source_currency_code} = X ${rateDetails.dest_currency_code}`
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          {rateDetails.rate}
                          {rateDetails.is_reversed && (
                            <div style={{ fontSize: '12px', color: '#586069', marginTop: 4 }}>
                              (Reverse: {rateDetails.dest_currency_code} per 1 {rateDetails.source_currency_code})
                            </div>
                          )}
                        </div>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Spread">
                      {editState.editing ? (
                        <InputNumber
                          value={editState.spread}
                          onChange={val => setEditState(prev => ({ ...prev, spread: val }))}
                          min={0}
                          style={{ width: 120 }}
                        />
                      ) : (
                        rateDetails.spread
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Spread Type">
                      {editState.editing ? (
                        <Select
                          value={editState.spread_type || rateDetails.spread_type || 'flat'}
                          onChange={val => setEditState(prev => ({ ...prev, spread_type: val }))}
                          style={{ width: 120 }}
                          options={[
                            { label: 'Flat', value: 'flat' },
                            { label: 'Percentage', value: 'percentage' },
                          ]}
                        />
                      ) : (
                        (rateDetails.spread_type || 'flat').charAt(0).toUpperCase() + (rateDetails.spread_type || 'flat').slice(1)
                      )}
                    </Descriptions.Item>
                    {editState.editing && (
                      <Descriptions.Item label="Rate Entry Mode">
                        <div style={{ marginBottom: 4 }}>
                          <div role="radiogroup" aria-label="rate entry mode" style={{ display: 'flex', gap: 16 }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="edit-rate-mode"
                                checked={!editState.is_reversed}
                                onChange={() => setEditState(prev => ({ ...prev, is_reversed: false }))}
                              />
                              Standard (1 {rateDetails.source_currency_code} = X {rateDetails.dest_currency_code})
                            </label>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="edit-rate-mode"
                                checked={editState.is_reversed}
                                onChange={() => setEditState(prev => ({ ...prev, is_reversed: true }))}
                              />
                              Reverse ({rateDetails.source_currency_code} per 1 {rateDetails.dest_currency_code})
                            </label>
                          </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#586069', marginTop: 4, lineHeight: '1.4' }}>
                          {editState.is_reversed ? (
                            `Enter how many ${rateDetails.source_currency_code} equal 1 ${rateDetails.dest_currency_code} (e.g., 1500)`
                          ) : (
                            `Enter rate as 1 ${rateDetails.source_currency_code} = X ${rateDetails.dest_currency_code} (e.g., 0.0006)`
                          )}
                        </div>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Total Rate">
                      {rateDetails.total_rate}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated">
                      {formatDate(rateDetails.updated)}
                    </Descriptions.Item>
                  </Descriptions>
                  {rateDetails?.bands && rateDetails.bands.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <Typography.Title level={5}>Rate Bands</Typography.Title>
                      <Table
                        dataSource={rateDetails.bands}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        columns={[
                          { title: 'Min Amount', dataIndex: 'minimum_amount', key: 'minimum_amount' },
                          { title: 'Max Amount', dataIndex: 'maximum_amount', key: 'maximum_amount' },
                          { title: 'Rate', dataIndex: 'rate', key: 'rate' },
                          { title: 'Spread', dataIndex: 'spread', key: 'spread' },
                          { 
                            title: 'Spread Type', 
                            dataIndex: 'spread_type', 
                            key: 'spread_type',
                            render: (spreadType) => spreadType.charAt(0).toUpperCase() + spreadType.slice(1)
                          },
                        ]}
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  )}
                  {hasSuperAdminAccess && (
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                      {editState.editing ? (
                        <>
                          <Button
                            type="primary"
                            loading={editState.saving}
                            onClick={saveEdit}
                            style={{ marginRight: 8 }}
                          >
                            Save
                          </Button>
                          <Button
                            onClick={() => setEditState(prev => ({ 
                              ...prev, 
                              editing: false, 
                              rate: rateDetails.rate, 
                              spread: rateDetails.spread,
                              spread_type: rateDetails.spread_type || 'flat'
                            }))}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="primary"
                          icon={<EditOutlined />}
                          onClick={() => setEditState(prev => ({ ...prev, editing: true }))}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  )}

                </div>
              ) : null}
            </Modal>
            <Modal
              title="Create Exchange Rate"
              open={createModalVisible}
              onCancel={() => {
                setCreateModalVisible(false);
                setIsReversed(false);
                createForm.resetFields();
              }}
              footer={null}
              destroyOnClose
            >
              <Form
                form={createForm}
                layout="vertical"
                onFinish={handleCreate}
              >
                <Form.Item
                  label="Source Currency Code"
                  name="source_currency_code"
                  rules={[{ required: true, message: "Source currency code is required" }]}
                >
                  <Input placeholder="e.g. USD" maxLength={3} style={{ textTransform: "uppercase" }} />
                </Form.Item>
                <Form.Item
                  label="Destination Currency Code"
                  name="dest_currency_code"
                  rules={[{ required: true, message: "Destination currency code is required" }]}
                >
                  <Input placeholder="e.g. NGN" maxLength={3} style={{ textTransform: "uppercase" }} />
                </Form.Item>
                <Form.Item
                  label="Rate"
                  name="rate"
                  rules={[{ required: true, message: "Rate is required" }]}
                >
                  <InputNumber 
                    min={0} 
                    style={{ width: "100%" }} 
                    placeholder={isReversed ? "e.g. 1500 (NGN per 1 USD)" : "e.g. 0.0006 (1 USD = X NGN)"} 
                  />
                </Form.Item>
                <Form.Item>
                  <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f8fa', borderRadius: 6, border: '1px solid #e1e4e8' }}>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontWeight: 500, marginBottom: 6 }}>Entry mode</div>
                      <div role="radiogroup" aria-label="rate entry mode" style={{ display: 'flex', gap: 16 }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="rate-mode"
                            checked={!isReversed}
                            onChange={() => setIsReversed(false)}
                          />
                          Standard (1 Source = X Dest)
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="rate-mode"
                            checked={isReversed}
                            onChange={() => setIsReversed(true)}
                          />
                          Reverse (Dest per 1 Source)
                        </label>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: '#586069', lineHeight: '1.4' }}>
                      {isReversed ? (
                        <>
                          <strong>Reverse mode:</strong> Enter how many destination currency units equal 1 source currency unit.
                          Example: For USD→NGN, enter 1500 to mean "1500 NGN = 1 USD".
                        </>
                      ) : (
                        <>
                          <strong>Standard mode:</strong> Enter the rate as 1 source currency = X destination currency units.
                          Example: For USD→NGN, enter 0.0006 to mean "1 USD = 0.0006 NGN".
                        </>
                      )}
                    </div>
                  </div>
                </Form.Item>
                <Form.Item label="Spread" name="spread">
                  <InputNumber min={0} style={{ width: "100%" }} placeholder="Optional" />
                </Form.Item>
                <Form.Item label="Spread Type" name="spread_type">
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select spread type"
                    options={[
                      { label: 'Flat', value: 'flat' },
                      { label: 'Percentage', value: 'percentage' },
                    ]}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={creating} block>
                    Create
                  </Button>
                </Form.Item>
              </Form>
            </Modal>

            {/* Filter Modal */}
            <Modal
              title="Filter Exchange Rates"
              open={filterModalVisible}
              onCancel={() => setFilterModalVisible(false)}
              footer={[
                <Button key="clear" onClick={clearFilters}>
                  Clear
                </Button>,
                <Button key="apply" type="primary" onClick={applyFilters}>
                  Apply
                </Button>
              ]}
              destroyOnClose
            >
              <div style={{ padding: '16px 0' }}>
                <Form layout="vertical">
                  <Form.Item label="Source Currency">
                    <Input
                      placeholder="Filter by source currency (e.g., USD)"
                      value={filterInputs.source_currency_code}
                      onChange={(e) => handleFilterInputChange('source_currency_code', e.target.value)}
                      onKeyPress={(e) => handleFilterKeyPress(e, 'source_currency_code')}
                      maxLength={3}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Item>
                  <Form.Item label="Destination Currency">
                    <Input
                      placeholder="Filter by destination currency (e.g., NGN)"
                      value={filterInputs.dest_currency_code}
                      onChange={(e) => handleFilterInputChange('dest_currency_code', e.target.value)}
                      onKeyPress={(e) => handleFilterKeyPress(e, 'dest_currency_code')}
                      maxLength={3}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Item>
                </Form>
                
                {hasActiveFilters && (
                  <div style={{ marginTop: 16, padding: '12px', background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      <FilterOutlined /> Active filters: 
                      {filters.source_currency_code && ` Source: ${filters.source_currency_code}`}
                      {filters.source_currency_code && filters.dest_currency_code && ' | '}
                      {filters.dest_currency_code && ` Destination: ${filters.dest_currency_code}`}
                    </Text>
                  </div>
                )}
              </div>
            </Modal>
          </StyledCard>
        </Col>
      </Row>
    </ScrollContainer>
  );
}

export default ExchangeRate;
